import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import FormFooter from '@department-of-veterans-affairs/platform-forms/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import {
  WIZARD_STATUS as PLATFORM_WIZARD_STATUS,
  WIZARD_STATUS_RESTARTED,
  restartShouldRedirect,
} from 'platform/site-wide/wizard';
import { resetScreenerSession } from '../../all-claims/subtask/screenerSession';

import {
  DISABILITY_526_V2_ROOT_URL,
  FORM_STATUS_BDD,
  SAVED_SEPARATION_DATE,
  // v1 and v2 share the same wizard status key (see routes.jsx).
  WIZARD_STATUS,
} from '../../shared/constants/entryConstants';
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPoll from '../components/ConfirmationPoll';

import { definitions } from '../schema';
import submitFormFor from './submitForm';
import { transform, hasValidatedSha } from './submit-transformer';
import prefillTransformer from './prefill-transformer';
import { trackHasUserUploadedSha } from '../utils/tracking/bddShaTracking';
import {
  isActivelyServing,
  isServingComponentGuardAndReserves,
  isCurrentlyActivated,
  isServingComponent,
  isReceivingServicePay,
  isMilitaryRetiredPay,
  isTrainingPay,
  hasDirectDeposit,
  hasPrefilledBankAccount,
} from '../utils/pageDependencies';
// Reuse the live 526 statement-of-truth (type-to-sign) certification so the new
// flow captures the same signed acknowledgment on review & submit.
import getPreSubmitInfo from '../../all-claims/content/preSubmitInfo';
import { createOnNavBackToScreener } from '../../all-claims/subtask/utils';
import PreSubmitInfo from '../../shared/components/PreSubmitInfo';
import CustomReviewTopContent from '../components/CustomReviewTopContent';
import veteranInformation, {
  onNavForward as veteranInformationOnNavForward,
} from '../pages/veteranDetails/veteranInformation';
import contactInformation from '../pages/veteranDetails/contactInformation';
import housingSituation from '../pages/veteranDetails/housingSituation';
import terminallyIll from '../pages/veteranDetails/terminallyIll';
import militaryServiceHistory from '../pages/veteranDetails/militaryServiceHistory';
import componentServed from '../pages/veteranDetails/componentServed';
import activeServiceDates from '../pages/veteranDetails/activeServiceDates';
import currentActivation from '../pages/veteranDetails/currentActivation';
import federalOrders from '../pages/veteranDetails/federalOrders';
import anticipatedSeparation from '../pages/veteranDetails/anticipatedSeparation';
import obligationDates from '../pages/veteranDetails/obligationDates';
import { buildDisabilityConditionsWorkflow } from '../../all-claims/pages/disabilityConditions';
import { PAGE_TITLES } from '../constants';
import { hasRealNewOrSecondaryConditions } from '../../all-claims/utils';
import { isBDD } from '../utils/bdd';
import prisonerOfWar from '../pages/conditions/prisonerOfWar';
import prisonerOfWarEras from '../pages/conditions/prisonerOfWarEras';
import prisonerOfWarDates from '../pages/conditions/prisonerOfWarDates';
import additionalDisabilityBenefits from '../pages/conditions/additionalDisabilityBenefits';
import adaptiveHousing from '../pages/conditions/adaptiveHousing';
import adaptiveAuto from '../pages/conditions/adaptiveAuto';
import aidAndAttendance from '../pages/conditions/aidAndAttendance';
import individualUnemployability from '../pages/conditions/individualUnemployability';
import additionalDisabilityBenefitsSummary from '../pages/conditions/additionalDisabilityBenefitsSummary';
import { form0781PagesConfig } from '../../form0781/config';
// Step 4 (Supporting evidence) scaffolding — one module per implementation
// ticket so each can be filled in without reshaping this chapter config:
//   #151946 sectionIntroPages + vaMedicalRecordsPages
//   #151945 privateMedicalRecordsPages
//   #155514 uploadAndSummaryPages
import reviewErrors from '../reviewErrors';
import { getCustomValidationErrors } from '../utils/customValidationErrors';
import { verifyPrefilledZipCode } from '../../shared/utils/zipCodeData';
import { sectionIntroPages } from '../pages/supportingEvidence/sectionIntroPages';
import { bddEvidencePages } from '../pages/supportingEvidence/bddEvidencePages';
import { vaMedicalRecordsPages } from '../pages/supportingEvidence/vaMedicalRecordsPages';
import { privateMedicalRecordsPages } from '../pages/supportingEvidence/privateMedicalRecordsPages';
import { uploadAndSummaryPages } from '../pages/supportingEvidence/uploadAndSummaryPages';
import paymentInformation from '../pages/additionalInformation/paymentInformation';
import servicePay, {
  updateFormData as servicePayUpdateFormData,
} from '../pages/additionalInformation/servicePay';
import servicePayType, {
  updateFormData as servicePayTypeUpdateFormData,
} from '../pages/additionalInformation/servicePayType';
import waiverMilitaryRetiredPay from '../pages/additionalInformation/waiverMilitaryRetiredPay';
import waiverTrainingPay from '../pages/additionalInformation/waiverTrainingPay';
import directDeposit, {
  updateFormData as updateDirectDepositFormData,
} from '../pages/additionalInformation/directDeposit';
import otherPaymentOptions from '../pages/additionalInformation/otherPaymentOptions';
import paymentInformationReview, {
  updateFormData as updatePaymentInformationReviewFormData,
} from '../pages/additionalInformation/paymentInformationReview';

// Path a restart returns the user to.
const INTRODUCTION_PATH = '/introduction';

// FormStartControls doesn't reliably receive formConfig, so it often writes
// the restart flag under the platform default key instead of
// WIZARD_STATUS. Check both so no stale status goes unconsumed.
const WIZARD_STATUS_KEYS = [WIZARD_STATUS, PLATFORM_WIZARD_STATUS];
// Wrap sessionStorage access in try/catch: in restricted environments
// (e.g. storage disabled), sessionStorage.getItem can throw, which
// must not break the form load (matches v1's prepareRestartRedirect pattern).
const isRestartPending = () => {
  try {
    return WIZARD_STATUS_KEYS.map(key => restartShouldRedirect(key)).some(
      Boolean,
    );
  } catch (error) {
    return false; // storage access blocked
  }
};

// router.push from a restart can re-enter onFormLoaded synchronously before
// the consumed wizard status persists, so remember the destination for the
// rest of the current task.
let pendingRestartDestination = null;

// v2 has its own formId, and the version is chosen before this mounts, so any
// saved form loaded here is v2 — resume to returnUrl, unless a restart is in
// progress, in which case route to /introduction. FormVersionShell re-resolves
// the version there, so a deleted v2 draft with the flag off still falls back
// to v1.
const onFormLoaded = ({
  returnUrl,
  router,
  isStartingOver,
  currentLocation,
  formData,
}) => {
  if (isStartingOver) {
    if (pendingRestartDestination) {
      if (!currentLocation?.pathname?.endsWith(pendingRestartDestination)) {
        router.push(pendingRestartDestination);
      }
      return;
    }

    if (isRestartPending()) {
      try {
        sessionStorage.setItem(WIZARD_STATUS, WIZARD_STATUS_RESTARTED);
        // Do not carry the abandoned application's BDD answers into the new one.
        sessionStorage.removeItem(FORM_STATUS_BDD);
        sessionStorage.removeItem(SAVED_SEPARATION_DATE);
        resetScreenerSession();
      } catch (error) {
        // storage access blocked; still route to the introduction
      }
      pendingRestartDestination = INTRODUCTION_PATH;
      setTimeout(() => {
        pendingRestartDestination = null;
      }, 0);
      router.push(INTRODUCTION_PATH);
      return;
    }
  }

  // Fire-and-forget verification of a prefilled zip the user may never edit;
  // read at submit by getCustomValidationErrors.
  verifyPrefilledZipCode(formData?.mailingAddress?.zipCode);

  router.push(returnUrl);
};

const submitAllClaimsV2 = submitFormFor('disability-526-all-claims-v2');

// Submit-success tracking seam: runs the normal submission, then fires submit-time
// RUM events. Currently records the hasUserUploadedSha value sent to the API
// (computed by the same hasValidatedSha the transform uses) so the FE signal
// matches the wire payload and the vets-api log; add future submit-time events here.
const submitWithTracking = (form, formConfigParam, options) =>
  submitAllClaimsV2(form, formConfigParam, options).then(result => {
    trackHasUserUploadedSha({
      hasUserUploadedSha: hasValidatedSha(form?.data || {}),
      formData: form?.data || {},
    });
    return result;
  });

/** @type {FormConfig} */
const formConfig = {
  rootUrl: DISABILITY_526_V2_ROOT_URL,
  urlPrefix: '/',
  dev: {
    showNavLinks: true,
    collapsibleNavLinks: true,
  },
  submitUrl: `${environment.API_URL}/disability_benefits/v0/submissions`,
  submit: submitWithTracking,
  transformForSubmit: transform,
  trackingPrefix: 'disability-526-all-claims-v2-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPoll,
  footerContent: FormFooter,
  formId: VA_FORM_IDS.FORM_21_526EZ_V2,
  // Matches the key onFormLoaded checks above (shared /start wizard with v1).
  wizardStorageKey: WIZARD_STATUS,
  saveInProgress: {
    messages: {
      inProgress:
        'Your disability compensation application (21-526EZ) is in progress.',
      expired:
        'Your saved disability compensation application (21-526EZ) has expired.',
      saved: 'Your disability compensation application has been saved.',
    },
    // Only reached when the post-delete prefill comes back completely empty;
    // the platform calls this directly instead of onFormLoaded in that case.
    restartFormCallback: () => {
      resetScreenerSession();
      return INTRODUCTION_PATH;
    },
  },
  version: 0,
  prefillEnabled: true,
  prefillTransformer,
  onFormLoaded,
  v3SegmentedProgressBar: true,
  // Render the Back/Continue nav buttons and the "Finish this application
  // later" link as V3 web components (va-button / va-link) instead of the
  // legacy raw <button> imposters.
  formOptions: {
    useWebComponentForNavigation: true,
  },
  savedFormMessages: {
    notFound: 'Please start over to file for disability compensation.',
    noAuth:
      'Please sign in again to resume your application for disability compensation.',
  },
  // BDD claims get "File a Benefits Delivery at Discharge claim"; all others get
  // "File for disability compensation" (same as the live 526 flow).
  title: ({ formData }) => PAGE_TITLES[isBDD(formData) ? 'BDD' : 'ALL'],
  subTitle:
    'Disability Compensation and Related Compensation Benefits (VA Form 21-526EZ)',
  defaultDefinitions: definitions,
  reviewErrors,
  customValidationErrors: getCustomValidationErrors,
  showReviewErrors: true,
  // Type-to-sign statement of truth on review & submit (VaStatementOfTruth web
  // component), matching the live 526 flow.
  preSubmitInfo: {
    ...getPreSubmitInfo(),
    CustomComponent: PreSubmitInfo,
  },
  CustomReviewTopContent,
  chapters: {
    veteranDetails: {
      title: 'Veteran details',
      pages: {
        veteranInformation: {
          path: 'veteran-information',
          title: 'Veteran information',
          onNavForward: veteranInformationOnNavForward,
          onNavBack: createOnNavBackToScreener('v2'),
          uiSchema: veteranInformation.uiSchema,
          schema: veteranInformation.schema,
        },
        contactInformation: {
          path: 'contact-information',
          title: 'Veteran contact information',
          uiSchema: contactInformation.uiSchema,
          schema: contactInformation.schema,
          updateFormData: contactInformation.updateFormData,
        },
        housingSituation: {
          path: 'housing-situation',
          title: 'Housing situation',
          uiSchema: housingSituation.uiSchema,
          schema: housingSituation.schema,
        },
        terminallyIll: {
          path: 'terminally-ill',
          title: 'Terminal illness',
          uiSchema: terminallyIll.uiSchema,
          schema: terminallyIll.schema,
        },
        militaryServiceHistory: {
          path: 'military-service-history',
          title: 'Military service history',
          uiSchema: militaryServiceHistory.uiSchema,
          schema: militaryServiceHistory.schema,
        },
        componentServed: {
          path: 'component-served',
          title: 'Component served',
          uiSchema: componentServed.uiSchema,
          schema: componentServed.schema,
        },
        mostRecentService: {
          path: 'active-service-dates',
          title: 'Most recent active service dates',
          depends: isActivelyServing,
          uiSchema: activeServiceDates.uiSchema,
          schema: activeServiceDates.schema,
          CustomPage: activeServiceDates.CustomPage,
          CustomPageReview: null,
          appStateSelector: state => ({ dob: state.user?.profile?.dob }),
        },
        federalOrders: {
          path: 'federal-orders',
          title: 'Federal orders',
          depends: isServingComponentGuardAndReserves,
          uiSchema: federalOrders.uiSchema,
          schema: federalOrders.schema,
        },
        currentActivation: {
          path: 'current-activation',
          title: 'Current activation',
          depends: isServingComponent,
          uiSchema: currentActivation.uiSchema,
          schema: currentActivation.schema,
        },
        anticipatedSeparation: {
          path: 'federal-activation-separation-date',
          title: 'Federal activation separation date',
          depends: isCurrentlyActivated,
          uiSchema: anticipatedSeparation.uiSchema,
          schema: anticipatedSeparation.schema,
        },
        obligationDates: {
          path: 'obligation-term-of-service',
          title: 'Obligation term of service',
          depends: isServingComponent,
          uiSchema: obligationDates.uiSchema,
          schema: obligationDates.schema,
        },
      },
    },
    conditions: {
      title: 'Conditions',
      pages: {
        ...buildDisabilityConditionsWorkflow({
          monthYearOnlyConditionDate: true,
          causeNewPageOptions: {
            hint: 'For example, "In 2022, I served near a burn pit in Iraq, which caused my bronchitis."',
            buildTitle: conditionName =>
              `Briefly describe the injury, event, disease, or exposure that caused ${conditionName}. Include the date and location details if you have them.`,
          },
        }),
        // prisoner of war (POW) is gated on at least one non-placeholder condition
        // that has cause type NEW or SECONDARY (consistent with the v1 flow)
        prisonerOfWar: {
          path: 'pow',
          title: 'Prisoner of war (POW)',
          depends: formData => hasRealNewOrSecondaryConditions(formData),
          uiSchema: prisonerOfWar.uiSchema,
          schema: prisonerOfWar.schema,
        },
        prisonerOfWarEras: {
          path: 'pow-eras',
          title: 'POW era or campaign',
          depends: formData =>
            hasRealNewOrSecondaryConditions(formData) &&
            formData?.powStatus === true,
          uiSchema: prisonerOfWarEras.uiSchema,
          schema: prisonerOfWarEras.schema,
        },
        prisonerOfWarDates: {
          path: 'pow-dates',
          title: 'Period of time held as a POW',
          depends: formData =>
            hasRealNewOrSecondaryConditions(formData) &&
            formData?.powStatus === true,
          uiSchema: prisonerOfWarDates.uiSchema,
          schema: prisonerOfWarDates.schema,
        },
        additionalDisabilityBenefits: {
          path: 'additional-disability-benefits',
          title: 'Additional disability benefits',
          uiSchema: additionalDisabilityBenefits.uiSchema,
          schema: additionalDisabilityBenefits.schema,
        },
        adaptiveHousing: {
          path: 'adaptive-housing',
          title: 'Adapted housing grants',
          depends: formData => formData?.['view:ancillaryFormsWizard'] === true,
          uiSchema: adaptiveHousing.uiSchema,
          schema: adaptiveHousing.schema,
        },
        adaptiveAuto: {
          path: 'adaptive-auto',
          title: 'Automobile allowance benefits',
          depends: formData => formData?.['view:ancillaryFormsWizard'] === true,
          uiSchema: adaptiveAuto.uiSchema,
          schema: adaptiveAuto.schema,
        },
        aidAndAttendance: {
          path: 'aid-and-attendance',
          title: 'Aid and Attendance benefits or Housebound allowance',
          depends: formData => formData?.['view:ancillaryFormsWizard'] === true,
          uiSchema: aidAndAttendance.uiSchema,
          schema: aidAndAttendance.schema,
        },
        individualUnemployability: {
          path: 'individual-unemployability',
          title: 'Individual Unemployability benefits',
          depends: formData => formData?.['view:ancillaryFormsWizard'] === true,
          uiSchema: individualUnemployability.uiSchema,
          schema: individualUnemployability.schema,
        },
        additionalDisabilityBenefitsSummary: {
          path: 'additional-disability-benefits-summary',
          title: 'Additional benefits you may qualify for',
          depends: formData =>
            formData?.['view:ancillaryFormsWizard'] === true &&
            !!(
              formData?.['view:modifyingHome'] ||
              formData?.['view:modifyingCar'] ||
              formData?.['view:aidAndAttendance'] ||
              formData?.['view:unemployable']
            ),
          uiSchema: additionalDisabilityBenefitsSummary.uiSchema,
          schema: additionalDisabilityBenefitsSummary.schema,
        },
      },
    },
    mentalHealth: {
      title: 'Mental health statement',
      pages: {
        ...form0781PagesConfig,
      },
    },
    supportingEvidence: {
      title: 'Supporting evidence',
      // Page order encodes the flow: intro screens, then VA medical records
      // (prioritized first when both record types are selected), then private
      // medical records, then uploads/summary/outro.
      pages: {
        ...sectionIntroPages,
        ...bddEvidencePages,
        ...vaMedicalRecordsPages,
        ...privateMedicalRecordsPages,
        ...uploadAndSummaryPages,
      },
    },
    additionalInformation: {
      title: 'Additional information',
      pages: {
        directDeposit: {
          path: 'direct-deposit',
          title: 'Direct deposit',
          depends: formData => !hasPrefilledBankAccount(formData),
          uiSchema: directDeposit.uiSchema,
          schema: directDeposit.schema,
          updateFormData: updateDirectDepositFormData,
        },
        paymentInformation: {
          path: 'set-up-direct-deposit',
          title: 'Set up direct deposit',
          depends: formData =>
            !hasPrefilledBankAccount(formData) && hasDirectDeposit(formData),
          uiSchema: paymentInformation.uiSchema,
          schema: paymentInformation.schema,
          CustomPageReview: () => null,
        },
        otherPaymentOptions: {
          path: 'payment-options',
          title: 'Payment options',
          depends: formData =>
            !hasPrefilledBankAccount(formData) && !hasDirectDeposit(formData),
          uiSchema: otherPaymentOptions.uiSchema,
          schema: otherPaymentOptions.schema,
        },
        paymentInformationReview: {
          path: 'review-direct-deposit',
          title: 'Review direct deposit',
          depends: formData =>
            hasPrefilledBankAccount(formData) || hasDirectDeposit(formData),
          CustomPage: paymentInformationReview.CustomPage,
          CustomPageReview: paymentInformationReview.CustomPageReview,
          uiSchema: paymentInformationReview.uiSchema,
          schema: paymentInformationReview.schema,
          updateFormData: updatePaymentInformationReviewFormData,
        },
        servicePay: {
          path: 'service-pay',
          title: 'Service pay',
          updateFormData: servicePayUpdateFormData,
          uiSchema: servicePay.uiSchema,
          schema: servicePay.schema,
        },
        servicePayType: {
          path: 'service-pay-type',
          title: 'Type of service pay',
          depends: formData => isReceivingServicePay(formData),
          updateFormData: servicePayTypeUpdateFormData,
          uiSchema: servicePayType.uiSchema,
          schema: servicePayType.schema,
        },
        waiverMilitaryRetiredPay: {
          path: 'waiver-military-retired-pay',
          title: 'Waiver for military retired pay',
          depends: formData =>
            isReceivingServicePay(formData) && isMilitaryRetiredPay(formData),
          uiSchema: waiverMilitaryRetiredPay.uiSchema,
          schema: waiverMilitaryRetiredPay.schema,
        },
        waiverTrainingPay: {
          path: 'waiver-training-pay',
          title: 'Waiver for active or inactive duty training pay',
          depends: formData =>
            isReceivingServicePay(formData) && isTrainingPay(formData),
          uiSchema: waiverTrainingPay.uiSchema,
          schema: waiverTrainingPay.schema,
        },
      },
    },
  },
};

export default formConfig;
